import { useState } from "react";

/* ─── SAMPLE BLOG DATA (same source as blog listing page) ─── */
const blogPosts = [
  {
    id: 1,
    category: "Fashion Trends",
    title: "Spring Collection 2026: Where Nature Meets Elegance",
    excerpt:
      "Discover how Vedoncia International is redefining fashion with earth-inspired palettes and sustainable fabrics that speak to the modern woman.",
    author: "Priya Sharma",
    authorRole: "Senior Fashion Editor",
    authorImg: "https://i.pravatar.cc/80?img=47",
    date: "May 10, 2026",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1200&q=80",
    tag: "New",
    content: [
      {
        type: "intro",
        text: "Spring 2026 arrives with a breath of fresh air — quite literally. Vedoncia International's latest collection draws deep from nature's own palette: moss greens, blush terracotta, soft ivory, and the delicate blush of cherry blossoms. This isn't just fashion; it's a conversation between heritage craft and contemporary consciousness.",
      },
      {
        type: "heading",
        text: "The Inspiration Behind the Collection",
      },
      {
        type: "paragraph",
        text: "Our creative director, Anjali Mehta, spent three weeks in the foothills of Uttarakhand — observing the way morning light filters through rhododendron forests, the texture of damp moss on stone, the geometry of terraced fields. Every observation became a design note. The result is a collection that feels organic yet intentional, effortless yet considered.",
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=900&q=80",
        caption: "Hand-woven khadi in the collection's signature sage green, photographed at our Jaipur studio.",
      },
      {
        type: "heading",
        text: "Fabrics That Breathe",
      },
      {
        type: "paragraph",
        text: "Sustainability is no longer a buzzword for us — it is the foundation. Every fabric in the Spring 2026 collection is sourced from certified organic weavers across Rajasthan, Bengal, and Assam. We use GOTS-certified cotton, plant-dyed silks, and hand-block-printed linens that improve with every wash.",
      },
      {
        type: "quote",
        text: "Fashion should leave the earth better than it found it. We're not just making clothes — we're making a promise.",
        attribution: "Anjali Mehta, Creative Director",
      },
      {
        type: "heading",
        text: "Key Pieces to Watch",
      },
      {
        type: "paragraph",
        text: "The standout piece this season is the Sage Anarkali — a floor-length silhouette in hand-woven chanderi with delicate aari embroidery along the yoke. It moves with the wearer rather than against her. Paired with our hand-block-print palazzo pants and a structured linen blazer, it becomes a full look suitable from boardroom to celebration.",
      },
      {
        type: "paragraph",
        text: "Also unmissable: the Terra Rosa kurta set in organic cotton mulmul, available in a gradient from blush to deep mauve. The fabric is so light it feels invisible — a quality we've spent two years perfecting with our artisan partners in Lucknow.",
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1594938298603-c8148c4b4f7e?w=900&q=80",
        caption: "The Terra Rosa kurta set — available exclusively on Vedoncia International from May 15.",
      },
      {
        type: "heading",
        text: "How to Style the Collection",
      },
      {
        type: "paragraph",
        text: "These pieces are designed to layer, mix, and interpret. A sage anarkali can be belted and worn as a maxi dress. The palazzo pants work with a simple white cotton shirt for day or a brocade blouse for evening. Our philosophy is that luxury should be versatile — pieces that earn their place in your wardrobe by working harder than any single-occasion garment.",
      },
      {
        type: "paragraph",
        text: "Shop the full Spring 2026 collection online and in all Vedoncia International stores from May 15, 2026. Early access is available for Vedoncia Circle members beginning May 12.",
      },
    ],
    tags: ["Spring 2026", "Sustainable Fashion", "Indian Wear", "Collection"],
  },
];

/* ─── RELATED POSTS ─── */
const relatedPosts = [
  {
    id: 2,
    category: "Lifestyle",
    title: "The Art of Mindful Shopping: Quality Over Quantity",
    date: "May 5, 2026",
    readTime: "4 min read",
    image: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=400&q=80",
  },
  {
    id: 3,
    category: "Sustainability",
    title: "Green Fashion: Our Promise to the Planet",
    date: "April 28, 2026",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400&q=80",
  },
  {
    id: 4,
    category: "Style Guide",
    title: "10 Ways to Style a Kurta for Every Occasion",
    date: "April 20, 2026",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1594938298603-c8148c4b4f7e?w=400&q=80",
  },
];

/* ─── COLOR TOKENS ─── */
const GREEN       = "#3d9970";
const LIGHT_GREEN = "#e8f5ee";
const MID_GREEN   = "#c1e8cf";
const DARK_GREEN  = "#1a5c3a";
const WHITE       = "#ffffff";
const SOFT        = "#f7fcf9";

export default function VedonciaInternationalBlogDetail({ postId = 1, onBack }) {
  const post = blogPosts.find((p) => p.id === postId) || blogPosts[0];
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(148);

  const handleLike = () => {
    setLiked((prev) => !prev);
    setLikeCount((prev) => (liked ? prev - 1 : prev + 1));
  };

  const renderBlock = (block, i) => {
    switch (block.type) {
      case "intro":
        return <p key={i} style={styles.intro}>{block.text}</p>;
      case "heading":
        return (
          <h2 key={i} style={styles.contentHeading}>
            <span style={styles.headingAccent}>—</span> {block.text}
          </h2>
        );
      case "paragraph":
        return <p key={i} style={styles.paragraph}>{block.text}</p>;
      case "quote":
        return (
          <blockquote key={i} style={styles.blockquote}>
            <p style={styles.quoteText}>"{block.text}"</p>
            <cite style={styles.quoteCite}>— {block.attribution}</cite>
          </blockquote>
        );
      case "image":
        return (
          <figure key={i} style={styles.contentFigure}>
            <img src={block.src} alt={block.caption} style={styles.contentImg} />
            <figcaption style={styles.figCaption}>{block.caption}</figcaption>
          </figure>
        );
      default:
        return null;
    }
  };

  return (
    <div style={styles.page}>

      {/* ── BACK BUTTON ── */}
      <div style={styles.backBar}>
        <div style={styles.container}>
          <button
            style={styles.backBtn}
            onClick={onBack}
          >
            ← Back to Blog
          </button>
        </div>
      </div>

      {/* ── HERO IMAGE ── */}
      <div style={styles.heroWrap}>
        <img src={post.image} alt={post.title} style={styles.heroImg} />
        <div style={styles.heroOverlay} />
        <div style={styles.heroText}>
          <span style={styles.heroCat}>{post.category}</span>
          <h1 style={styles.heroTitle}>{post.title}</h1>
          <p style={styles.heroExcerpt}>{post.excerpt}</p>
        </div>
      </div>

      {/* ── META BAR ── */}
      <div style={styles.metaBar}>
        <div style={{ ...styles.container, ...styles.metaInner }}>
          <div style={styles.authorBlock}>
            <img src={post.authorImg} alt={post.author} style={styles.authorImg} />
            <div>
              <div style={styles.authorName}>{post.author}</div>
              <div style={styles.authorRole}>{post.authorRole}</div>
            </div>
          </div>
          <div style={styles.metaRight}>
            <span style={styles.metaItem}>📅 {post.date}</span>
            <span style={styles.metaDot}>·</span>
            <span style={styles.metaItem}>⏱ {post.readTime}</span>
          </div>
        </div>
      </div>

      {/* ── ARTICLE BODY ── */}
      <div style={styles.articleWrap}>
        <div style={styles.container}>
          <div style={styles.articleGrid}>

            {/* MAIN CONTENT */}
            <article style={styles.article}>
              {post.content.map((block, i) => renderBlock(block, i))}

              {/* TAGS */}
              <div style={styles.tagsRow}>
                <span style={styles.tagsLabel}>Tags:</span>
                {post.tags.map((tag) => (
                  <span key={tag} style={styles.tagPill}>{tag}</span>
                ))}
              </div>

              {/* LIKE + SHARE */}
              <div style={styles.actionRow}>
                <button
                  style={{
                    ...styles.likeBtn,
                    ...(liked ? styles.likeBtnActive : {}),
                  }}
                  onClick={handleLike}
                >
                  {liked ? "💚" : "🤍"} {likeCount} Likes
                </button>
                <div style={styles.shareGroup}>
                  <span style={styles.shareLabel}>Share:</span>
                  {["Facebook", "Twitter", "WhatsApp", "Copy Link"].map((s) => (
                    <button key={s} style={styles.shareBtn}>{s}</button>
                  ))}
                </div>
              </div>

              {/* AUTHOR CARD */}
              <div style={styles.authorCard}>
                <img src={post.authorImg} alt={post.author} style={styles.authorCardImg} />
                <div style={styles.authorCardBody}>
                  <div style={styles.authorCardLabel}>Written by</div>
                  <div style={styles.authorCardName}>{post.author}</div>
                  <div style={styles.authorCardRole}>{post.authorRole} at Vedoncia International</div>
                  <p style={styles.authorCardBio}>
                    Priya covers fashion, sustainability, and the cultural stories behind Indian craft. She has been writing for Vedoncia International since 2022 and is passionate about slow fashion and mindful dressing.
                  </p>
                </div>
              </div>
            </article>

            {/* SIDEBAR */}
            <aside style={styles.sidebar}>

              {/* TABLE OF CONTENTS */}
              <div style={styles.sideCard}>
                <div style={styles.sideCardTitle}>📋 In This Article</div>
                <ul style={styles.tocList}>
                  {post.content
                    .filter((b) => b.type === "heading")
                    .map((b, i) => (
                      <li key={i} style={styles.tocItem}>
                        <span style={styles.tocDot} />
                        {b.text}
                      </li>
                    ))}
                </ul>
              </div>

              
              {/* TAGS */}
              <div style={styles.sideCard}>
                <div style={styles.sideCardTitle}>🏷 Popular Tags</div>
                <div style={styles.sideTagsWrap}>
                  {["Fashion", "Sustainability", "Style Guide", "Indian Wear", "Lifestyle", "Heritage", "Artisans"].map((t) => (
                    <span key={t} style={styles.sideTag}>{t}</span>
                  ))}
                </div>
              </div>

            </aside>
          </div>
        </div>
      </div>

      {/* ── RELATED POSTS ── */}
      <section style={styles.relatedSection}>
        <div style={styles.container}>
          <div style={styles.labelRow}>
            <div style={styles.bar} />
            <span style={styles.label}>You May Also Like</span>
          </div>
          <div style={styles.relatedGrid}>
            {relatedPosts.map((rp) => (
              <div key={rp.id} style={styles.relatedCard}>
                <div style={styles.relatedImgWrap}>
                  <img src={rp.image} alt={rp.title} style={styles.relatedImg} />
                </div>
                <div style={styles.relatedBody}>
                  <span style={styles.relatedCat}>{rp.category}</span>
                  <h4 style={styles.relatedTitle}>{rp.title}</h4>
                  <div style={styles.relatedMeta}>
                    <span>{rp.date}</span>
                    <span>·</span>
                    <span>{rp.readTime}</span>
                  </div>
                  <button style={styles.relatedBtn}>Read →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}

/* ─────────── STYLES ─────────── */
const styles = {
  page: {
    fontFamily: "'Georgia', 'Garamond', serif",
    background: SOFT,
    color: "#1a2e22",
  },
  container: { maxWidth: 1200, margin: "0 auto" },

  /* Back bar */
backBar: {
    background: WHITE,
    borderBottom: `1px solid ${MID_GREEN}`,
    padding: "12px 24px",
    position: "sticky",
    top: 0,
    zIndex: 999,
  },
  backBtn: {
    background: "transparent",
    border: `1.5px solid ${MID_GREEN}`,
    borderRadius: 20,
    padding: "6px 18px",
    fontSize: 13,
    color: GREEN,
    fontWeight: 700,
    cursor: "pointer",
  },

  /* Hero */
  heroWrap: {
    position: "relative",
    height: "clamp(320px, 55vw, 560px)",
    overflow: "hidden",
  },
  heroImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  heroOverlay: {
    position: "absolute", inset: 0,
    background: "linear-gradient(to top, rgba(10,40,20,0.85) 0%, rgba(10,40,20,0.3) 50%, transparent 100%)",
  },
  heroText: {
    position: "absolute",
    bottom: 0, left: 0, right: 0,
    padding: "40px clamp(20px, 5vw, 80px)",
    maxWidth: 820,
  },
  heroCat: {
    display: "inline-block",
    background: GREEN,
    color: WHITE,
    borderRadius: 20,
    padding: "4px 14px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: "uppercase",
    marginBottom: 14,
  },
  heroTitle: {
    fontSize: "clamp(22px, 4vw, 48px)",
    fontWeight: 700,
    color: WHITE,
    margin: "0 0 14px",
    lineHeight: 1.2,
  },
  heroExcerpt: {
    color: "rgba(255,255,255,0.82)",
    fontSize: "clamp(14px, 2vw, 17px)",
    lineHeight: 1.65,
    margin: 0,
    maxWidth: 680,
  },

  /* Meta bar */
  metaBar: {
    background: WHITE,
    borderBottom: `2px solid ${LIGHT_GREEN}`,
    padding: "0 24px",
  },
  metaInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    padding: "16px 0",
  },
  authorBlock: { display: "flex", alignItems: "center", gap: 12 },
  authorImg: { width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: `2px solid ${MID_GREEN}` },
  authorName: { fontWeight: 700, fontSize: 15, color: DARK_GREEN },
  authorRole: { fontSize: 12, color: "#7aab8f" },
  metaRight: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#7aab8f" },
  metaItem: {},
  metaDot: { color: MID_GREEN },

  /* Article layout */
  articleWrap: { padding: "48px 24px 32px" },
  articleGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 320px",
    gap: 48,
    alignItems: "start",
  },

  /* Article content */
  article: { maxWidth: "100%" },
  intro: {
    fontSize: 19,
    lineHeight: 1.85,
    color: "#2e4a38",
    fontStyle: "italic",
    borderLeft: `4px solid ${GREEN}`,
    paddingLeft: 20,
    marginBottom: 36,
  },
  contentHeading: {
    fontSize: "clamp(18px, 2.5vw, 24px)",
    fontWeight: 700,
    color: DARK_GREEN,
    margin: "40px 0 16px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headingAccent: { color: GREEN, fontSize: 20 },
  paragraph: {
    fontSize: 16,
    lineHeight: 1.9,
    color: "#3a5244",
    marginBottom: 22,
  },
  blockquote: {
    background: LIGHT_GREEN,
    border: `none`,
    borderLeft: `5px solid ${GREEN}`,
    borderRadius: "0 12px 12px 0",
    padding: "24px 28px",
    margin: "32px 0",
  },
  quoteText: {
    fontSize: 18,
    fontStyle: "italic",
    color: DARK_GREEN,
    lineHeight: 1.7,
    margin: "0 0 10px",
  },
  quoteCite: { fontSize: 13, color: GREEN, fontStyle: "normal", fontWeight: 700 },
  contentFigure: { margin: "32px 0" },
  contentImg: {
    width: "100%",
    borderRadius: 12,
    display: "block",
    objectFit: "cover",
    maxHeight: 420,
  },
  figCaption: {
    textAlign: "center",
    fontSize: 13,
    color: "#7aab8f",
    marginTop: 10,
    fontStyle: "italic",
  },

  /* Tags */
  tagsRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, margin: "36px 0 28px" },
  tagsLabel: { fontSize: 13, fontWeight: 700, color: "#7aab8f" },
  tagPill: {
    background: LIGHT_GREEN,
    border: `1px solid ${MID_GREEN}`,
    borderRadius: 20,
    padding: "4px 14px",
    fontSize: 12,
    color: DARK_GREEN,
    fontWeight: 600,
  },

  /* Actions */
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    padding: "20px 0",
    borderTop: `1px solid ${LIGHT_GREEN}`,
    borderBottom: `1px solid ${LIGHT_GREEN}`,
    marginBottom: 36,
  },
  likeBtn: {
    background: WHITE,
    border: `1.5px solid ${MID_GREEN}`,
    borderRadius: 30,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 700,
    color: "#3a5244",
    cursor: "pointer",
  },
  likeBtnActive: {
    background: LIGHT_GREEN,
    borderColor: GREEN,
    color: DARK_GREEN,
  },
  shareGroup: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  shareLabel: { fontSize: 13, color: "#7aab8f", fontWeight: 700 },
  shareBtn: {
    background: WHITE,
    border: `1px solid ${MID_GREEN}`,
    borderRadius: 16,
    padding: "5px 12px",
    fontSize: 12,
    color: GREEN,
    cursor: "pointer",
    fontWeight: 600,
  },

  /* Author card */
  authorCard: {
    display: "flex",
    gap: 20,
    background: WHITE,
    border: `1px solid ${MID_GREEN}`,
    borderRadius: 16,
    padding: 24,
    alignItems: "flex-start",
  },
  authorCardImg: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    objectFit: "cover",
    border: `3px solid ${MID_GREEN}`,
    flexShrink: 0,
  },
  authorCardBody: {},
  authorCardLabel: { fontSize: 11, color: "#7aab8f", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 },
  authorCardName: { fontSize: 18, fontWeight: 700, color: DARK_GREEN, marginBottom: 2 },
  authorCardRole: { fontSize: 13, color: GREEN, marginBottom: 10 },
  authorCardBio: { fontSize: 14, color: "#5a7a68", lineHeight: 1.7, margin: 0 },

  /* Sidebar */
  sidebar: { position: "sticky", top: 20, display: "flex", flexDirection: "column", gap: 20 },
  sideCard: {
    background: WHITE,
    border: `1px solid ${MID_GREEN}`,
    borderRadius: 14,
    padding: 20,
  },
  sideCardTitle: { fontSize: 13, fontWeight: 700, color: DARK_GREEN, marginBottom: 14, letterSpacing: 0.5 },
  tocList: { listStyle: "none", padding: 0, margin: 0 },
  tocItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    fontSize: 14,
    color: "#3a5244",
    padding: "7px 0",
    borderBottom: `1px solid ${LIGHT_GREEN}`,
    lineHeight: 1.45,
    cursor: "pointer",
  },
  tocDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: GREEN,
    flexShrink: 0,
    marginTop: 5,
  },

  /* Newsletter sidebar */
  sideNewsletter: {
    background: `linear-gradient(135deg, ${DARK_GREEN}, ${GREEN})`,
    borderRadius: 14,
    padding: 22,
    textAlign: "center",
  },
  nlTitle: { fontSize: 17, fontWeight: 700, color: WHITE, marginBottom: 6 },
  nlSub: { fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: 1.5, marginBottom: 14 },
  nlInput: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 8,
    border: "none",
    fontSize: 13,
    marginBottom: 10,
    outline: "none",
    boxSizing: "border-box",
  },
  nlBtn: {
    width: "100%",
    padding: "10px",
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.4)",
    borderRadius: 8,
    color: WHITE,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },

  /* Sidebar tags */
  sideTagsWrap: { display: "flex", flexWrap: "wrap", gap: 8 },
  sideTag: {
    background: LIGHT_GREEN,
    border: `1px solid ${MID_GREEN}`,
    borderRadius: 16,
    padding: "4px 12px",
    fontSize: 12,
    color: DARK_GREEN,
    cursor: "pointer",
  },

  /* Related */
  relatedSection: { padding: "16px 24px 64px" },
  labelRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 24 },
  bar: { width: 4, height: 22, background: GREEN, borderRadius: 2 },
  label: { fontSize: 12, fontWeight: 700, color: GREEN, letterSpacing: 2, textTransform: "uppercase" },
  relatedGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 24,
  },
  relatedCard: {
    background: WHITE,
    borderRadius: 14,
    overflow: "hidden",
    border: `1px solid ${MID_GREEN}`,
    boxShadow: "0 4px 16px rgba(61,153,112,0.07)",
    display: "flex",
    flexDirection: "column",
  },
  relatedImgWrap: { height: 180 },
  relatedImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  relatedBody: { padding: "16px 18px 18px", flex: 1, display: "flex", flexDirection: "column" },
  relatedCat: { fontSize: 10, fontWeight: 700, color: GREEN, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 },
  relatedTitle: { fontSize: 15, fontWeight: 700, color: DARK_GREEN, margin: "0 0 8px", lineHeight: 1.35, flex: 1 },
  relatedMeta: { display: "flex", gap: 6, fontSize: 12, color: "#7aab8f", marginBottom: 12 },
  relatedBtn: {
    background: "transparent",
    border: `1.5px solid ${GREEN}`,
    borderRadius: 20,
    padding: "5px 16px",
    fontSize: 12,
    color: GREEN,
    fontWeight: 700,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
};